import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiClock, FiExternalLink, FiSearch, FiX } from "react-icons/fi";
import type { LogEntry } from "../../API/Models";
import { badgeForLog, buildLogLink } from "../../Helper/activityAction";
import { formatRelativeTimeFr } from "../../Helper/dates";
import { LEVEL_OPTIONS, PERIOD_OPTIONS, TYPE_OPTIONS, pageCount, pageRangeLabel, sinceForPeriod, type PeriodKey } from "../../Helper/activityFilters";
import type { ActivityFilter, ActivityPage } from "./accountClient";

/**
 * Journal d'activité d'UN utilisateur, paginé côté serveur et filtrable (action, niveau, période,
 * texte) : le même composant sert la page Profil (« Mon compte » et /account/:id) et l'onglet
 * Activité de la fiche « Gérer » de la page Utilisateurs. Le parent fournit la fonction de
 * chargement (elle connaît l'utilisateur visé) ; ici vivent les filtres, la pagination et le rendu.
 */

const PAGE_SIZES = [20, 50, 100];
const SEARCH_DEBOUNCE_MS = 350;

const fullDate = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });

type Props = {
	/** Charge une page filtrée ; le parent lie l'utilisateur (soi-même ou un id). */
	fetchPage: (page: number, pageSize: number, filter: ActivityFilter) => Promise<ActivityPage>;
	/** Faux = l'appelant n'a pas la permission : message calme, aucun appel serveur. */
	allowed?: boolean;
	/** Taille de page initiale. */
	initialPageSize?: number;
	/** Rendu resserré (dans une modale). */
	compact?: boolean;
	/** Bump pour forcer un rechargement (après une action sur le compte). */
	refreshKey?: number;
};

export const UserActivityJournal: React.FC<Props> = ({ fetchPage, allowed = true, initialPageSize = 20, compact = false, refreshKey = 0 }) => {
	const [type, setType] = useState("");
	const [level, setLevel] = useState("");
	const [period, setPeriod] = useState<PeriodKey>("all");
	const [search, setSearch] = useState("");
	const [debounced, setDebounced] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [data, setData] = useState<ActivityPage | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const seq = useRef(0);

	useEffect(() => {
		const h = window.setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => window.clearTimeout(h);
	}, [search]);

	// Tout changement de filtre ramène à la première page.
	useEffect(() => {
		setPage(1);
	}, [type, level, period, debounced, pageSize]);

	const filter = useMemo<ActivityFilter>(() => ({ type, level, since: sinceForPeriod(period), q: debounced }), [type, level, period, debounced]);

	const load = useCallback(async () => {
		if (!allowed) return;
		const mine = ++seq.current;
		setLoading(true);
		setError(null);
		try {
			const result = await fetchPage(page, pageSize, filter);
			if (mine !== seq.current) return;
			setData(result);
		} catch (e: any) {
			if (mine !== seq.current) return;
			setError(e?.response?.data?.message ?? "Impossible de charger l'activité.");
		} finally {
			if (mine === seq.current) setLoading(false);
		}
	}, [allowed, fetchPage, page, pageSize, filter]);

	useEffect(() => {
		void load();
	}, [load, refreshKey]);

	if (!allowed) {
		return <p className="px-4 py-4 text-sm text-slate-400">Vous n'avez pas l'autorisation de consulter l'activité de cet utilisateur.</p>;
	}

	const total = data?.total ?? 0;
	const pages = pageCount(total, pageSize);
	const items = data?.items ?? [];
	const hasFilter = !!(type || level || debounced || period !== "all");
	const pad = compact ? "px-3" : "px-4";

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			{/* Filtres : action, niveau, période, texte, sur une rangée. */}
			<div className={`flex flex-wrap items-center gap-2 border-b border-slate-100 ${pad} py-2.5`}>
				<select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type d'action" className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200">
					{TYPE_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
				<select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Niveau" className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200">
					{LEVEL_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
				<div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label="Période">
					{PERIOD_OPTIONS.map((p) => (
						<button
							key={p.key}
							type="button"
							onClick={() => setPeriod(p.key)}
							aria-pressed={period === p.key}
							className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition ${period === p.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
						>
							{p.label}
						</button>
					))}
				</div>
				<label className="relative ml-auto block min-w-[160px] flex-1 sm:flex-none">
					<FiSearch className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
					<input
						type="search"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Rechercher dans l'activité…"
						aria-label="Rechercher dans l'activité"
						className="w-full rounded-lg border border-slate-200 bg-white py-1 pl-7 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
					/>
				</label>
				{hasFilter && (
					<button
						type="button"
						onClick={() => {
							setType("");
							setLevel("");
							setPeriod("all");
							setSearch("");
						}}
						className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
					>
						<FiX className="h-3 w-3" aria-hidden /> Réinitialiser
					</button>
				)}
			</div>

			{/* Liste */}
			<div className="min-h-0 flex-1 overflow-y-auto">
				{error ? (
					<p className={`${pad} py-4 text-sm text-rose-600`}>{error}</p>
				) : loading && !data ? (
					<ul className={`space-y-2 ${pad} py-3`} aria-hidden>
						{Array.from({ length: 6 }).map((_, i) => (
							<li key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />
						))}
					</ul>
				) : items.length === 0 ? (
					<p className={`${pad} py-6 text-center text-sm text-slate-400`}>{hasFilter ? "Aucune activité ne correspond à ces filtres." : "Aucune activité enregistrée."}</p>
				) : (
					<ul className={`divide-y divide-slate-100 ${loading ? "opacity-60" : ""}`} aria-busy={loading}>
						{items.map((entry, i) => {
							const badge = badgeForLog(entry);
							const link = buildLogLink(entry);
							const when = entry.createdAt ?? entry.updatedAt;
							return (
								<li key={entry.id ?? i} className={`${pad} py-2`}>
									<div className="flex items-center gap-2">
										<span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge.chip}`}>{badge.label}</span>
										<span className="inline-flex items-center gap-1 text-[11px] text-slate-400" title={when ? fullDate.format(new Date(when.endsWith("Z") ? when : when + "Z")) : ""}>
											<FiClock className="text-[10px]" /> {when ? formatRelativeTimeFr(when) : ""}
										</span>
										{entry.level && /warn|error/i.test(entry.level) && (
											<span className={`rounded px-1.5 py-px text-[10px] font-semibold uppercase ${/error/i.test(entry.level) ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"}`}>{/error/i.test(entry.level) ? "erreur" : "avertissement"}</span>
										)}
										{link && (
											<Link to={link} className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 hover:underline" title="Ouvrir le contenu concerné">
												Ouvrir <FiExternalLink className="h-3 w-3" aria-hidden />
											</Link>
										)}
									</div>
									<p className="mt-0.5 truncate text-sm text-slate-700" title={entry.message ?? ""}>
										{entry.message}
									</p>
								</li>
							);
						})}
					</ul>
				)}
			</div>

			{/* Pagination */}
			<div className={`flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 ${pad} py-2 text-xs text-slate-500`}>
				<span className="tabular-nums">{pageRangeLabel(page, pageSize, total)}</span>
				<div className="flex items-center gap-2">
					<label className="flex items-center gap-1">
						<span className="sr-only">Par page</span>
						<select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Éléments par page" className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
							{PAGE_SIZES.map((n) => (
								<option key={n} value={n}>
									{n} / page
								</option>
							))}
						</select>
					</label>
					<div className="flex items-center gap-0.5">
						<button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} aria-label="Page précédente" className="rounded-md border border-slate-200 bg-white p-1 text-slate-600 disabled:opacity-40">
							<FiChevronLeft className="h-3.5 w-3.5" />
						</button>
						<span className="min-w-[64px] text-center tabular-nums">
							{page} / {pages}
						</span>
						<button type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages || loading} aria-label="Page suivante" className="rounded-md border border-slate-200 bg-white p-1 text-slate-600 disabled:opacity-40">
							<FiChevronRight className="h-3.5 w-3.5" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UserActivityJournal;
